import { useCallback, useEffect, useState } from 'react';
import { Layout } from '@/components/layout';
import { OrderCard } from '@/components/domain';
import { EmptyState, ErrorState, Spinner } from '@/components/atoms';
import { useBackButton } from '@/hooks';
import { orders as ordersApi } from '@/api';
import type { Order, OrderStatus } from '@/types/domain';
import { logger } from '@/utils/logger';
import styles from './pages.module.css';

// F-180 C2: UA tab labels per QA cleanup spec.
const TABS: { value: string; label: string; filter: OrderStatus[] }[] = [
  { value: 'inbox', label: 'Вхідні', filter: ['proposed'] },
  { value: 'active', label: 'Активні', filter: ['accepted', 'in_progress'] },
  { value: 'completed', label: 'Завершені', filter: ['completed', 'reviewed'] },
  { value: 'all', label: 'Усі', filter: [] },
];

export function MyCrmPage(): JSX.Element {
  useBackButton();
  const [tab, setTab] = useState<(typeof TABS)[number]['value']>('inbox');
  const [items, setItems] = useState<Order[] | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refetch = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setItems(null);
      setError(null);
      try {
        const data = await ordersApi.fetchOrders({ role: 'specialist' });
        if (!cancelled) setItems(data.results);
      } catch (err) {
        logger.warn('CRM fetch failed', err);
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setItems(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const activeTab = TABS.find((t) => t.value === tab) ?? TABS[0]!;
  const filtered =
    items === null
      ? null
      : activeTab.filter.length === 0
        ? items
        : items.filter((o) => activeTab.filter.includes(o.status));

  return (
    <Layout title="CRM">
      <div className={styles.tabs}>
        {TABS.map((t) => (
          <button
            key={t.value}
            className={[styles.tab, tab === t.value && styles.tabActive].filter(Boolean).join(' ')}
            onClick={() => setTab(t.value)}
          >
            {t.label}
          </button>
        ))}
      </div>
      {error ? (
        <ErrorState onRetry={refetch} />
      ) : filtered === null ? (
        <Spinner center />
      ) : filtered.length === 0 ? (
        <EmptyState icon="crm" message="У цій вкладці поки порожньо." />
      ) : (
        <div className={styles.list}>
          {filtered.map((o) => (
            <OrderCard key={o.id} order={o} />
          ))}
        </div>
      )}
    </Layout>
  );
}
